// The recorder's lifecycle half, mounted at the App root (App.tsx) whether
// or not anyone is signed in: a sign-out, an expired session that drops to
// the sign-in page or an error screen never takes these away while a
// meeting records (decisions D1). It gives the recorder the page's last
// seconds on pagehide and checks the microphone on the way back, keeps a
// waiting build (and the chunk-recovery reload) from reloading the page,
// asks before a close or reload in a browser that asks, and in the Mac app
// hands over stopRecording / flushRecording and tells the app what is
// recording (never before the store has been looked in once). Only binding
// the recorder to a workspace lives in the signed-in tree (RecorderProvider).

import { useEffect } from "react";

import { useUpdateHold } from "@/components/pwa/updateHold";
import { isMacShell, setRecordingControls } from "@/native/desktop";
import { announceRecording } from "@/native/desktopRecording";
import { recorder } from "./recorderMedia";
import { setRecordingActive } from "./recordingHold";
import { useRecorder } from "./useRecorder";

export function RecorderLifecycle() {
  const r = useRecorder();

  // The page's last seconds on the way out; the microphone checked on the way back.
  useEffect(() => {
    const eng = recorder();
    const onHide = () => eng.pageHidden(true);
    const onVisibility = () => (document.visibilityState === "hidden" ? eng.pageHidden(false) : eng.pageShown());
    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onVisibility);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onVisibility);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // A reload while the microphone is live, or while parts wait to go, holds (updates, chunk recovery).
  const busy = r.capturing || r.shellPhase !== "idle" || r.unsent > 0;
  useUpdateHold(busy);
  useEffect(() => setRecordingActive(busy), [busy]);

  // A close or reload stops the microphone: browsers that can ask, ask. (What was
  // captured is kept on the device and sent by the next page; with no device
  // storage the queue itself would be lost, so it asks for that too.)
  const guard = r.capturing || (busy && r.storage === "memory");
  useEffect(() => {
    if (!guard) return;
    const ask = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [guard]);

  // The Mac app: its quit / reload / sleep / sign-out guards and its menu bar follow this.
  useEffect(() => {
    if (!isMacShell()) return;
    const eng = recorder();
    const announce = (fresh: boolean) => {
      const m = eng.shellMessage();
      if (m) announceRecording(m, fresh);
    };
    setRecordingControls({
      stop: () => void eng.stopAndUpload(),
      flush: () => void eng.flush().then(() => eng.scan()).then(() => announce(true), () => announce(true)),
    });
    const off = eng.subscribe(() => announce(false));
    void eng.scan().catch(() => undefined);
    return () => {
      off();
      setRecordingControls(null);
    };
  }, []);

  return null;
}
